import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Button,
  Tag,
  Form,
  Card,
  Skeleton,
  Tooltip,
  Typography,
} from "antd";
import { InfoCircleOutlined, LeftCircleFilled } from "@ant-design/icons";
import { getOrderDetails } from "apis/orderDetails";
import {
  fetchBundleComponentTransfers,
  retryBundlePrismEnrolment,
} from "apis/bundleProgramOrder";
import { fetchDetailsByBillingRequestIdApi } from "apis/order";
import styles from "./index.scss";

// import ValueAddedServiceDrawer from "./components/ValueAddedServiceDrawer";
import InfoTable from "./components/InfoTable";
import DetailsSections from "./components/DetailsSections";
import {
  formatRevenueAmount,
  formatRevenuePercent,
  hasFailedBundleComponent,
  isRevenueSplitHeadingRow,
  isRevenueSplitTotalRow,
  prismRetryKey,
  prismRetryMessage,
  revenueSplitRows,
  transformBundleComponentTransfers,
  transformOrderDetailsResponse,
} from "./utils";
import Toast from "widgets/Toast";

import useTenantHook from "hooks/useTenantHook";

import { useDispatch } from "react-redux";

import routePaths from "app/components/Router/routePaths";
import { redirect } from "actions/navigation";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import TransactionTable from "../TransactionTableKH/index";
import {
  OrderDetailsProvider,
  useOrderDetails,
} from "../../../../../contexts/OrderDetailsContext";
import TransferOwnershipDrawer from "./components/TransferOwnership";

const toast = Toast();

const OrderDetailsContent = () => {
  const dispatch = useDispatch();
  const params = useParams();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  // const [valueAddedDrawerVisible, setValueAddedDrawerVisible] = useState(false);
  const [transferOwnershipDrawerVisible, setTransferOwnershipDrawerVisible] =
    useState(false);
  // OOD-895: the Prism retry in flight (order line + component, see prismRetryKey),
  // so only the button that was pressed shows a spinner
  const [prismRetryingKey, setPrismRetryingKey] = useState(null);
  const { currentTenantCode, isKHTenant } = useTenantHook();
  // Bundle Program Move History (OOD-895) - empty for every other order
  const [moveHistoryRows, setMoveHistoryRows] = useState([]);
  const [moveHistoryLoading, setMoveHistoryLoading] = useState(false);
  const { setFormData, formData, toggleEditMode, updateField } =
    useOrderDetails();

  const getTransferButtonProperties = useCallback(() => {
    if (!formData)
      return {
        disabled: true,
        tooltipText: "",
        type: "primary",
        className: styles["order-details__transfer-button-is-not-transfered"],
      };

    // Set type and className based on transfer status
    const isTransferred =
      formData?.transferHistory?.transferType === "OWNERSHIP_TRANSFER";
    const type = isTransferred ? "text" : "primary";
    const className = isTransferred
      ? styles["order-details__transfer-button-is-transfered"]
      : styles["order-details__transfer-button-is-not-transfered"];

    // If already transferred, don't disable the button
    if (isTransferred) {
      return {
        disabled: false,
        tooltipText: "",
        type,
        className,
      };
    }

    if (
      formData?.order?.orderStatus === "DRAFT" ||
      formData?.order?.orderStatus === "DEFERRED"
    ) {
      return {
        disabled: true,
        tooltipText: `Order is in ${formData?.order?.orderStatus?.toLowerCase()} state`,
        type,
        className,
      };
    }

    const scheduleStartDate = formData?.program?.[0]?.scheduleStartDate;
    const currentDate = new Date();

    let startDate;
    if (
      typeof scheduleStartDate === "string" &&
      scheduleStartDate.includes("/")
    ) {
      const [day, month, year] = scheduleStartDate
        .split("/")
        .map((num) => parseInt(num, 10));
      startDate = new Date(year, month - 1, day);
    } else {
      startDate = new Date(scheduleStartDate);
    }

    if (startDate < currentDate) {
      return {
        disabled: true,
        tooltipText: "This batch has already started and cannot be transferred",
        type,
        className,
      };
    }

    return {
      disabled: false,
      tooltipText: "",
      type,
      className,
    };
  }, [formData]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const billingRequestId = params.billingRequestId;
      const response = await getOrderDetails(
        billingRequestId,
        currentTenantCode
      );
      const transformedData = transformOrderDetailsResponse(response.data);

      setFormData(transformedData);

      form.setFieldsValue({
        pricing: transformedData.pricing,
        order: transformedData.order,
        salesFields: transformedData.sales,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  }, [form, setFormData, currentTenantCode, params.billingRequestId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const summaryDataSource = formData?.summary?.map((item) => ({
    ...item,
    key: item.orderId,
  }));

  /**
   * OOD-895. Add-ons are their own order lines, so they get their own table rather
   * than rows in Program Details - that table's columns are schedules and delivery
   * modes, none of which an add-on has.
   */
  const addOnDataSource = formData?.addOns?.map((item, index) => ({
    ...item,
    // The code is not unique on an order: OSL is repeatable, so several lines can
    // share it. Index keeps React's keys distinct where the code would collide.
    key: `${item.addOnCode}-${index}`,
  }));

  const addOnColumns = [
    {
      title: "Add-on",
      dataIndex: "name",
      key: "name",
      render: renderTableCell,
    },
    {
      title: "Code",
      dataIndex: "addOnCode",
      key: "addOnCode",
      render: renderTableCell,
    },
    {
      title: "Bought with",
      dataIndex: "parentOfferingKey",
      key: "parentOfferingKey",
      // An add-on bought on its own has no parent, and "N/A" would read as missing
      // data rather than as the deliberate state it is.
      render: (val) => <span>{val ?? "Sold on its own"}</span>,
    },
    {
      title: "Price",
      dataIndex: "priceWithoutTax",
      key: "priceWithoutTax",
      render: renderTableCell,
    },
    {
      title: "Tax",
      dataIndex: "taxAmount",
      key: "taxAmount",
      render: renderTableCell,
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      render: renderTableCell,
    },
    {
      title: "Paid",
      dataIndex: "payableAmount",
      key: "payableAmount",
      render: renderTableCell,
    },
    {
      title: "Split weight",
      dataIndex: "discountSplitWeight",
      key: "discountSplitWeight",
      // Answers "why did this add-on get 500 off and not 1,000". Absent on orders
      // placed before OOD-895, where nothing was stamped.
      render: (val) => <span>{val ?? "—"}</span>,
    },
  ];

  const renderTableCell = (val) => {
    return <span>{val ?? "N/A"}</span>;
  };

  const summaryColumns = [
    {
      title: "Order Id",
      dataIndex: "orderId",
      key: "orderId",
      render: renderTableCell,
    },
    {
      title: "Payment Source",
      dataIndex: "paymentSource",
      key: "paymentSource",
      render: (val) => {
        return val ? (
          <Button
            type="link"
            className={styles["order-details__payment-source"]}
          >
            {val}
          </Button>
        ) : (
          "N/A"
        );
      },
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: renderTableCell,
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: renderTableCell,
    },
    {
      title: "Created by",
      dataIndex: "createdBy",
      key: "createdBy",
      render: renderTableCell,
    },
    {
      title: "Created on",
      dataIndex: "createdOn",
      key: "createdOn",
      render: renderTableCell,
    },
    {
      title: "Sales agent",
      dataIndex: "salesAgent",
      key: "salesAgent",
      render: renderTableCell,
    },
  ];

  const programDataSource = formData?.program?.map((item, index) => ({
    ...item,
    // bundle component rows can share a programName - key must stay unique
    key: `${item.offeringKey || item.programName}-${item.scheduleId || index}`,
  }));

  // Bundle Program order (OOD-895): rows carry per-component split + status
  const isBundleProgramOrder = formData?.program?.some(
    (item) => item.revenueSplit || item.bundleComponentStatus
  );

  /**
   * Move History (OOD-895): every component move of this order's chain, from
   * order-service. It is addressed by the group order id, which the billing
   * request details carry. Bundle orders on KH only - nothing is fetched for
   * any other order.
   */
  useEffect(() => {
    if (!isKHTenant || !isBundleProgramOrder) return undefined;
    let cancelled = false;
    const loadMoveHistory = async () => {
      try {
        setMoveHistoryLoading(true);
        const details = await fetchDetailsByBillingRequestIdApi(
          params.billingRequestId
        );
        const groupOrderId =
          details?.data?.groupOrder?.id ||
          details?.data?.billing?.groupOrderId ||
          null;
        if (!groupOrderId) return;
        const transfers = await fetchBundleComponentTransfers(groupOrderId);
        if (!cancelled) {
          setMoveHistoryRows(
            transformBundleComponentTransfers(transfers?.data)
          );
        }
      } catch (e) {
        // the history is additional information: never fail the order page
        if (!cancelled) setMoveHistoryRows([]);
      } finally {
        if (!cancelled) setMoveHistoryLoading(false);
      }
    };
    loadMoveHistory();
    return () => {
      cancelled = true;
    };
  }, [isKHTenant, isBundleProgramOrder, params.billingRequestId]);

  const BUNDLE_STATUS_COLORS = {
    Pending: "default",
    Enrolled: "blue",
    Locked: "orange",
    Completed: "green",
    Deferred: "gold",
    Failed: "red",
  };

  // OOD-895: re-run Prism enrolment for a bundle order. With a component
  // offering key only that component is sent; without one, every component.
  // Convergent on the BE (already-enrolled components are untouched), so a
  // retry is always safe.
  const handlePrismRetry = async (orderEntityId, componentOfferingKey) => {
    try {
      setPrismRetryingKey(prismRetryKey(orderEntityId, componentOfferingKey));
      const { data } = await retryBundlePrismEnrolment(
        orderEntityId,
        componentOfferingKey
      );
      const message = prismRetryMessage(data, componentOfferingKey);
      if (message.level === "warning") {
        toast.warning(message.text);
      } else {
        toast.success(message.text);
      }
      fetchData();
    } catch (error) {
      console.error("Prism enrolment retry failed:", error);
      toast.error("Could not retry Prism enrolment. Please try again.");
    } finally {
      setPrismRetryingKey(null);
    }
  };

  // The bundle's order line id, shared by every component row of the order
  const bundleOrderEntityId = formData?.program?.find(
    (item) => item.bundleOrderEntityId
  )?.bundleOrderEntityId;
  const showRetryAll =
    !!bundleOrderEntityId && hasFailedBundleComponent(formData?.program);

  const programColumns = [
    {
      title: "Program Name",
      dataIndex: "programName",
      key: "programName",
      render: renderTableCell,
    },
    {
      title: "Schedule ID",
      dataIndex: "scheduleId",
      key: "scheduleId",
      render: renderTableCell,
    },
    {
      title: "Offering key",
      dataIndex: "offeringKey",
      key: "offeringKey",
      render: renderTableCell,
    },
    {
      title: "Schedule Start Date",
      dataIndex: "scheduleStartDate",
      key: "scheduleStartDate",
      render: renderTableCell,
    },
    {
      title: "Learning Type",
      dataIndex: "learningType",
      key: "learningType",
      render: renderTableCell,
    },
    {
      title: "Workshop Location",
      dataIndex: "workshopLocation",
      key: "workshopLocation",
      render: renderTableCell,
    },
    // Bundle Program columns (OOD-895): appended only for bundle orders so
    // the regular order table stays exactly as before. The money columns read
    // as a ledger per component, all ex-tax except Cash Collected:
    // Actual Price - Discount (= taxable amount, not displayed); + Tax =
    // Cash Collected; - Tax = Final Revenue. The BE sends them as display
    // strings; componentTaxableAmount still arrives but has no column.
    ...(isBundleProgramOrder
      ? [
          {
            title: "Split %",
            dataIndex: "componentSplitPercent",
            key: "componentSplitPercent",
            render: renderTableCell,
          },
          {
            title: "Actual Price",
            dataIndex: "componentActualPrice",
            key: "componentActualPrice",
            render: renderTableCell,
          },
          {
            title: "Discount",
            dataIndex: "componentDiscount",
            key: "componentDiscount",
            render: renderTableCell,
          },
          {
            title: "Tax %",
            dataIndex: "componentTaxPercent",
            key: "componentTaxPercent",
            render: renderTableCell,
          },
          {
            title: "Tax",
            dataIndex: "componentTax",
            key: "componentTax",
            render: renderTableCell,
          },
          {
            title: "Cash Collected",
            dataIndex: "componentCashCollected",
            key: "componentCashCollected",
            render: renderTableCell,
          },
          {
            title: "Final Revenue",
            dataIndex: "componentFinalRevenue",
            key: "componentFinalRevenue",
            render: renderTableCell,
          },
          {
            title: (
              <>
                Status
                {showRetryAll && (
                  <Button
                    size="small"
                    type="link"
                    loading={
                      prismRetryingKey === prismRetryKey(bundleOrderEntityId)
                    }
                    onClick={() => handlePrismRetry(bundleOrderEntityId)}
                  >
                    Retry all
                  </Button>
                )}
              </>
            ),
            dataIndex: "bundleComponentStatus",
            key: "bundleComponentStatus",
            render: (value, record) =>
              value ? (
                <>
                  <Tag color={BUNDLE_STATUS_COLORS[value] || "default"}>
                    {value}
                  </Tag>
                  {value === "Failed" && record.bundleOrderEntityId && (
                    <Button
                      size="small"
                      type="link"
                      loading={
                        prismRetryingKey ===
                        prismRetryKey(
                          record.bundleOrderEntityId,
                          record.offeringKey
                        )
                      }
                      onClick={() =>
                        handlePrismRetry(
                          record.bundleOrderEntityId,
                          record.offeringKey
                        )
                      }
                    >
                      Retry
                    </Button>
                  )}
                </>
              ) : (
                renderTableCell(value)
              ),
          },
        ]
      : []),
  ];

  /**
   * Revenue Summary (OOD-895): the bundle's components, their subtotal,
   * the deferral fee when the bill carries one, the add-on lines sold with
   * the bundle and the grand total, each with its tax and cash collected, as one ledger. Built from the same response as Program Details; KH bundle orders
   * only, so every other order's page is exactly as before.
   */
  const orderCurrency = formData?.summary?.[0]?.currency;
  const revenueSplitDataSource =
    isBundleProgramOrder && isKHTenant
      ? revenueSplitRows(formData?.program, formData?.addOns)
      : [];
  // heading rows carry only their label; total rows are bold
  const renderRevenueSplitCell = (record, content) => {
    if (isRevenueSplitHeadingRow(record)) return null;
    return isRevenueSplitTotalRow(record) ? (
      <strong>{content}</strong>
    ) : (
      content
    );
  };
  const revenueSplitColumns = [
    {
      title: "Item",
      dataIndex: "item",
      key: "item",
      render: (value, record) =>
        isRevenueSplitHeadingRow(record) ? (
          <strong>{value}</strong>
        ) : (
          renderRevenueSplitCell(
            record,
            <>
              {renderTableCell(value)}
              {record.discountSplitWeight !== null &&
                record.discountSplitWeight !== undefined && (
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                    {`weight ${record.discountSplitWeight}`}
                  </Typography.Text>
                )}
            </>
          )
        ),
    },
    {
      title: "Revenue Before Discount",
      dataIndex: "revenueBeforeDiscount",
      key: "revenueBeforeDiscount",
      render: (value, record) =>
        renderRevenueSplitCell(
          record,
          renderTableCell(formatRevenueAmount(value, orderCurrency))
        ),
    },
    {
      title: "Discount Allocated",
      dataIndex: "discountAllocated",
      key: "discountAllocated",
      render: (value, record) =>
        renderRevenueSplitCell(
          record,
          renderTableCell(formatRevenueAmount(value, orderCurrency))
        ),
    },
    {
      title: "Discount %",
      dataIndex: "discountPercent",
      key: "discountPercent",
      render: (value, record) =>
        renderRevenueSplitCell(
          record,
          renderTableCell(formatRevenuePercent(value))
        ),
    },
    {
      title: "Tax",
      dataIndex: "tax",
      key: "tax",
      render: (value, record) =>
        renderRevenueSplitCell(
          record,
          renderTableCell(formatRevenueAmount(value, orderCurrency))
        ),
    },
    {
      title: "Cash Collected",
      dataIndex: "cashCollected",
      key: "cashCollected",
      render: (value, record) =>
        renderRevenueSplitCell(
          record,
          renderTableCell(formatRevenueAmount(value, orderCurrency))
        ),
    },
  ];

  // Bundle Program Move History columns (OOD-895)
  const moveHistoryColumns = [
    {
      title: "Seq",
      dataIndex: "sequence",
      key: "sequence",
      render: renderTableCell,
    },
    {
      title: "Component",
      dataIndex: "componentKey",
      key: "componentKey",
      render: renderTableCell,
    },
    {
      title: "From Schedule",
      dataIndex: "from",
      key: "from",
      render: renderTableCell,
    },
    {
      title: "To Schedule",
      dataIndex: "to",
      key: "to",
      render: renderTableCell,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: renderTableCell,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: renderTableCell,
    },
    {
      title: "Moved By",
      dataIndex: "actor",
      key: "actor",
      render: renderTableCell,
    },
    {
      title: "Moved On",
      dataIndex: "movedAt",
      key: "movedAt",
      render: renderTableCell,
    },
  ];

  const getSections = () => {
    const sections = [];

    const pricingFields = [
      {
        key: "priceWithoutTax",
        label: "Price Without Tax",
        value: loading ? "" : formData.pricing?.priceWithoutTax,
      },
      {
        key: "taxDisplay",
        label: "Tax",
        value: loading
          ? ""
          : `${formData.pricing?.tax} ${formData.pricing?.taxValue}`,
      },
      {
        key: "priceWithTax",
        label: "Price With Tax",
        value: loading ? "" : formData.pricing?.priceWithTax,
      },
    ];

    // if (currentTenantCode !== "UPGRAD") {
    //   pricingFields.push({
    //     key: "valueAddedService",
    //     label: "Value Added Service",
    //     value: loading ? "" : formData.pricing?.valueAddedService,
    //     type: loading ? undefined : "action",
    //     render: loading
    //       ? undefined
    //       : (value) => (
    //           <div style={{ display: "flex", alignItems: "center" }}>
    //             <Text>{value}</Text>
    //             <Button
    //               type="link"
    //               onClick={() => setValueAddedDrawerVisible(true)}
    //               style={{ padding: "0 4px" }}
    //             >
    //               (View Details)
    //             </Button>
    //           </div>
    //         ),
    //   });
    // }

    pricingFields.push({
      key: "discount",
      label: "Discount",
      render: loading
        ? undefined
        : (value) => (
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {value}
              <Tooltip title="This discount is the total of early bird discount (if any) and coupons applied on the schedule.">
                <InfoCircleOutlined
                  style={{ color: "#1890ff", fontSize: "14px" }}
                />
              </Tooltip>
            </span>
          ),
      value: loading ? "" : formData.pricing?.discount,
    });
    pricingFields.push({
      key: "grandPrice",
      label: "Grand Price",
      value: loading ? "" : formData.pricing?.grandPrice,
    });

    sections.push({
      key: "pricing",
      title: "Pricing Details",
      fields: pricingFields,
    });

    const orderFields = [
      {
        key: "modeOfPayment",
        label: "Mode Of Payment",
        value: loading ? "" : formData.order?.modeOfPayment,
      },
      {
        key: "lastPaymentDate",
        label: "Last Payment Date",
        value: loading ? "" : formData.order?.lastPaymentDate,
      },
      {
        key: "paidAmount",
        label: "Paid Amount",
        value: loading ? "" : formData.order?.paidAmount || "Active",
        render: loading
          ? undefined
          : (value) => <Tag color="success">{value}</Tag>,
      },
      {
        key: "orderIdBillingRequestId",
        label: "Order Id/ Billing Request ID",
        value: loading ? "" : formData.order?.orderIdBillingRequestId,
      },
      {
        key: "orderStatus",
        label: "Order Status",
        value: loading ? "" : formData.order?.orderStatus,
      },
      {
        key: "orderBillingDate",
        label: "Order/ Billing Date",
        value: loading ? "" : formData.order?.orderBillingDate,
      },
      {
        key: "applicationCompletedOn",
        label: "Application Completed On",
        value: loading ? "" : formData.order?.applicationCompletedOn,
      },
      {
        key: "applicationId",
        label: "Application ID",
        value: loading ? "" : formData.order?.applicationId,
      },
      {
        key: "applicationBatch",
        label: "Application Batch",
        value: loading ? "" : formData.order?.applicationBatch,
      },
    ];

    sections.push({
      key: "order",
      title: "Order Details",
      fields: orderFields,
    });

    return sections;
  };

  const handleSave = (sectionKey, fieldKey) => {
    const formValues = form.getFieldsValue();
    const newValue = formValues[sectionKey]?.[fieldKey];
    updateField(sectionKey, fieldKey, newValue);
  };

  const handleCancel = (sectionKey, fieldKey) => {
    toggleEditMode(sectionKey, fieldKey);
  };

  const handleTransferSuccess = () => {
    fetchData();
  };

  const transferButtonProps = getTransferButtonProperties();

  return (
    <Card className={styles["order-details__container"]}>
      <Row>
        <Col span={24}>
          <div
            className={styles["order-details__header"]}
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Button
              type="link"
              icon={<LeftCircleFilled />}
              className={styles["order-details__back-button"]}
              onClick={() =>
                dispatch(redirect(routePaths.userInfo__transactions))
              }
            >
              View Order
            </Button>
            {loading ? (
              <Skeleton.Button active style={{ width: 150, height: 32 }} />
            ) : (
              <Tooltip title={transferButtonProps.tooltipText}>
                <Button
                  type={transferButtonProps.type}
                  className={transferButtonProps.className}
                  onClick={() => setTransferOwnershipDrawerVisible(true)}
                  // disabled={transferButtonProps.disabled}
                >
                  {formData?.transferHistory?.transferType ===
                  "OWNERSHIP_TRANSFER"
                    ? "View Transferred Ownership Details"
                    : "Transfer Ownership"}
                </Button>
              </Tooltip>
            )}
          </div>

          <InfoTable
            title="Summary"
            dataSource={summaryDataSource}
            columns={summaryColumns}
            tag={formData?.tag}
            loading={loading}
          />

          {!loading && formData?.paymentLink && (
            // OOD-895 D4: the link that collects this order - on the order a
            // paid deferral moved the learner to, the deferral fee link
            <div style={{ margin: "0 0 16px" }}>
              <Typography.Text strong>
                {formData.paymentLink.label}:{" "}
              </Typography.Text>
              <Typography.Text
                copyable={{
                  text:
                    formData.paymentLink.url ||
                    formData.paymentLink.salesLinkId,
                }}
              >
                {formData.paymentLink.url || formData.paymentLink.salesLinkId}
              </Typography.Text>
              {formData.paymentLink.deferralFeeLink && (
                <Tag color="warning" style={{ marginLeft: 8 }}>
                  Deferral fee
                </Tag>
              )}
              {formData.paymentLink.linkStatus && (
                <Tag style={{ marginLeft: 8 }}>
                  {formData.paymentLink.linkStatus}
                </Tag>
              )}
            </div>
          )}

          <InfoTable
            title="Program Details"
            dataSource={programDataSource}
            columns={programColumns}
            loading={loading}
            // the bundle ledger is wide; let it scroll instead of squeezing every column
            scroll={isBundleProgramOrder ? { x: "max-content" } : undefined}
          />
          {revenueSplitDataSource.length > 0 && (
            <InfoTable
              title="Revenue Summary"
              dataSource={revenueSplitDataSource}
              columns={revenueSplitColumns}
              loading={loading}
              scroll={{ x: "max-content" }}
            />
          )}
          {isBundleProgramOrder && isKHTenant && moveHistoryRows.length > 0 && (
            <InfoTable
              title="Move History"
              dataSource={moveHistoryRows}
              columns={moveHistoryColumns}
              loading={moveHistoryLoading}
              scroll={{ x: "max-content" }}
            />
          )}          
          {/* OOD-895. Rendered only when the order actually has add-ons, so every
              order without them looks exactly as it does today. */}
          {addOnDataSource?.length > 0 && (
            <InfoTable
              title="Add-ons"
              dataSource={addOnDataSource}
              columns={addOnColumns}
              loading={loading}
            />
          )}
          <DetailsSections
            sections={getSections()}
            loading={loading}
            handleSave={handleSave}
            handleCancel={handleCancel}
            form={form}
          />
        </Col>
      </Row>

      <div className={styles["order-details__transaction-section"]}>
        <TransactionTable billingRequestId={params.billingRequestId} />
      </div>

      {/* {currentTenantCode !== "UPGRAD" && (
        <ValueAddedServiceDrawer
          visible={valueAddedDrawerVisible}
          onClose={() => setValueAddedDrawerVisible(false)}
        />
      )} */}

      <TransferOwnershipDrawer
        visible={transferOwnershipDrawerVisible}
        onClose={() => setTransferOwnershipDrawerVisible(false)}
        billingRequestId={params.billingRequestId}
        tenantCode={currentTenantCode}
        sourceUserId={formData?.summary?.[0]?.sourceUserId}
        transferHistory={formData?.transferHistory}
        currency={formData?.summary?.[0]?.currency}
        programKey={formData?.program?.[0]?.programKey}
        applicationBatch={formData?.order?.applicationBatch}
        onTransferSuccess={handleTransferSuccess}
      />
    </Card>
  );
};

const OrderDetails = () => {
  return (
    <OrderDetailsProvider>
      <OrderDetailsContent />
    </OrderDetailsProvider>
  );
};

export default OrderDetails;
